import React, { useState } from "react";
import "./Booking.css";

function Booking({ id }) {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    vehicle: "",
    service: "",
    washMethod: "",
    weatherCheck: false,
    address: "",
    notes: "",
  });

  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handlePhotoClick = () => {
    document.getElementById("photoUpload").click();
  };

  const handlePhotoChange = (e) => {
    const files = Array.from(e.target.files);

    if (files.length > 5) {
      setMessage({ 
        text: "Maximum 5 images allowed", 
        type: "error" 
      });
      return;
    }

    setPhotos(files);
    setMessage({ text: "", type: "" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); // 🚫 stop page refresh

    // Basic validation
    if (
      !formData.name ||
      !formData.phone ||
      !formData.email ||
      !formData.vehicle ||
      !formData.service ||
      !formData.washMethod ||
      !formData.address
    ) {
      setMessage({ 
        text: "Please fill all required fields (*)", 
        type: "error" 
      });
      return;
    }

    setLoading(true);
    setMessage({ text: "", type: "" });

    try {
      // Prepare the payload
      const payload = {
        ...formData,
        photos: photos.map(file => file.name),
      };

      console.log("📤 Booking payload:", payload);

      // Make API call
      const response = await fetch('https://luxego-m2zx.onrender.com/api/bookings', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.message || 'Failed to submit booking');
      }

      console.log("✅ Booking successful:", result);
      
      setMessage({ 
        text: result.message || "Booking submitted successfully!", 
        type: "success" 
      });

      // Reset form
      setFormData({
        name: "",
        phone: "",
        email: "",
        vehicle: "",
        service: "",
        washMethod: "",
        weatherCheck: false,
        address: "",
        notes: "",
      });
      setPhotos([]);

      // Clear success message after 5 seconds
      setTimeout(() => {
        setMessage({ text: "", type: "" });
      }, 5000);

    } catch (err) {
      console.error("❌ Booking error:", err);
      setMessage({ 
        text: err.message || "Something went wrong. Please try again.", 
        type: "error" 
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id={id} className="booking-section">
      <div className="booking-container">
        <div className="booking-header">
          <span className="booking-subtitle">BOOK YOUR SERVICE</span>
          <h2 className="booking-title">Schedule Your Detailing</h2>
          <p className="booking-desc">
            Fill out the form below and we'll come to your doorstep
          </p>
        </div>

        {/* Message display */}
        {message.text && (
          <div className={`message ${message.type}`}>
            {message.text}
          </div>
        )}

        <div className="booking-card">
          <form onSubmit={handleSubmit}>
            <div className="form-grid">
              <div className="field">
                <label>Full Name *</label>
                <input
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  type="text"
                  placeholder="John Smith"
                  required
                />
              </div>

              <div className="field">
                <label>Phone Number *</label>
                <input
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  type="text"
                  placeholder="07XXX XXXXXX"
                  required
                />
              </div>

              <div className="field full">
                <label>Email Address *</label>
                <input
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  type="email"
                  placeholder="john@example.com"
                  required
                />
              </div>

              <div className="field">
                <label>Vehicle Make & Model *</label>
                <input
                  name="vehicle"
                  value={formData.vehicle}
                  onChange={handleChange}
                  type="text"
                  placeholder="BMW 3 Series"
                  required
                />
              </div>

              <div className="field">
                <label>Select Service *</label>
                <select
                  name="service"
                  value={formData.service}
                  onChange={handleChange}
                  required
                >
                  <option value="">Choose a package</option>
                  <option>Luxego Premium Restoration</option>
                  <option>Luxego Standard Detailing</option>
                  <option>Luxego Exterior Focused</option>
                  <option>Luxego Interior Focused</option>
                  <option>Luxego Regular Maintenance</option>
                </select>
              </div>
            </div>

            <div className="sustainable-box">
              <h4>🌿 Sustainable Options</h4>

              <label>Wash Method *</label>
              <select
                name="washMethod"
                value={formData.washMethod}
                onChange={handleChange}
                required
              >
                <option value="">Choose wash method</option>
                <option>Waterless (Eco-Smart)</option>
                <option>Traditional Method</option>
              </select>

              <label className="checkbox">
                <input
                  type="checkbox"
                  name="weatherCheck"
                  checked={formData.weatherCheck}
                  onChange={handleChange}
                />
                <span>Run Weather Check</span>
              </label>
            </div>

            <div className="field full">
              <label>Your Location / Address *</label>
              <input
                name="address"
                value={formData.address}
                onChange={handleChange}
                type="text"
                placeholder="123 High Street"
                required
              />
            </div>

            <div className="field full">
              <label>Main Concerns / Special Requests</label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                placeholder="Stains, scratches, pet hair..."
              />
            </div>

            <div className="upload-box">
              <label>Before Pictures (Optional – Max 5)</label>

              <input
                type="file"
                id="photoUpload"
                multiple
                accept="image/*"
                hidden
                onChange={handlePhotoChange}
              />

              <div className="upload-placeholder" onClick={handlePhotoClick}>
                + Add Photos
              </div>

              {photos.length > 0 && (
                <div className="photo-preview">
                  {photos.map((file, i) => (
                    <span key={i}>{file.name}</span>
                  ))}
                </div>
              )}
            </div>

            <button type="submit" className="submit-btn" disabled={loading}>
              {loading ? "Submitting..." : "✈ Submit Booking Request"}
            </button>

            <p className="booking-note">
              We'll contact you within 24 hours
            </p>
          </form>
        </div>
      </div>
    </section>
  );
}

export default Booking;